"use client";
import { Button } from "@/components/ui/button/button";
import StepOne from "@/components/ui/form/buildForm/stepOne";
import StepTwo from "@/components/ui/form/buildForm/stepTwo";
import { TokenFormData, tokenFormSchema } from "@/lib/builderFormSchema";
import { yupResolver } from "@hookform/resolvers/yup";
import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import { Transaction } from "@solana/web3.js";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import React, { useCallback, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import TokenSuccessModalBuilder from "../../modal/tokenModalBuilder";
import ProgressBar from "./progressBar";
import StepThree from "./stepThree";
import { Icons } from "@/components/Icons";

const TOTAL_STEPS = 3;

const MemoizedStepOne = React.memo(StepOne);
const MemoizedStepTwo = React.memo(StepTwo);
const MemoizedStepThree = React.memo(StepThree);

const TokenBuildForm = () => {
  const [step, setStep] = useState(1);
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tokenAddress, setTokenAddress] = useState<string | null>(null);
  const { connected, publicKey, signTransaction } = useWallet();
  const { connection } = useConnection();

  const methods = useForm({
    resolver: yupResolver(tokenFormSchema),
    mode: "onChange",
  });

  const validateCurrentStep = useCallback(async () => {
    let fieldsToValidate: (keyof TokenFormData)[] = [];

    switch (step) {
      case 1:
        fieldsToValidate = [
          "tokenName",
          "tokenDescription",
          "tokenSymbol",
          "image",
        ];
        break;
      case 2:
        fieldsToValidate = [
          "vestingSchedule",
          "marketCap",
          "presaleVestingSchedule",
          "presaleAllocationPercent",
          "creatorAllocationPercent",
          "userPresaleLimitPercent",
        ];
        break;
      case 3:
        fieldsToValidate = [];
        break;
    }

    return methods.trigger(fieldsToValidate);
  }, [step, methods]);

  const currentStepComponent = useMemo(() => {
    switch (step) {
      case 1:
        return <MemoizedStepOne />;
      case 2:
        return <MemoizedStepTwo />;
      case 3:
        return <MemoizedStepThree />;
      default:
        return <MemoizedStepOne />;
    }
  }, [step]);

  const handleSubmit = useCallback(async () => {
    if (!connected || !publicKey) {
      toast.error("Please connect your wallet first", {
        id: "wallet-connect-error",
        duration: 4000,
      });
      return;
    }
    try {
      setIsSubmitting(true);
      const isValid = await methods.trigger();
      if (!isValid) {
        const errors = methods.formState.errors;
        console.error("❌ Form validation errors:", errors);
        return;
      }

      const formData = methods.getValues();

      const presalePercent = Number(formData.presaleAllocationPercent) || 60;
      const creatorPercent = Number(formData.creatorAllocationPercent) || 20;

      if (
        presalePercent < 0 ||
        presalePercent > 80 ||
        creatorPercent < 0 ||
        creatorPercent > 80
      ) {
        toast.error("Allocation percentages must be between 0% and 80%", {
          id: "allocation-error",
          duration: 5000,
        });
        return;
      }

      if (presalePercent + creatorPercent !== 80) {
        toast.error(
          `Presale and Creator allocations must sum to exactly 80%. Current total: ${
            presalePercent + creatorPercent
          }%`,
          {
            id: "allocation-error",
            duration: 5000,
          }
        );
        return;
      }

      const apiFormData = new FormData();
      if (formData.image && formData.image.length > 0) {
        apiFormData.append("image", formData.image[0]);
      }
      let presaleStartTime, presaleEndTime, publicStartTime;

      if (formData.presaleStartTime && formData.presaleEndTime) {
        presaleStartTime = Math.floor(
          new Date(formData.presaleStartTime).getTime() / 1000
        );
        presaleEndTime = Math.floor(
          new Date(formData.presaleEndTime).getTime() / 1000
        );
      } else {
        presaleStartTime = Math.floor(Date.now() / 1000) + 40; // Start in 40 seconds
        presaleEndTime = Math.floor(Date.now() / 1000) + 600; // End in 10 minutes
      }
      if (formData.enableCustomLaunch && formData.launchDate) {
        publicStartTime = Math.floor(
          new Date(formData.launchDate).getTime() / 1000
        );
      } else {
        // If custom presale times are set, make public launch relative to presale end
        // Otherwise use the default timing
        if (formData.presaleStartTime && formData.presaleEndTime) {
          publicStartTime = presaleEndTime + 60 * 10; // 10 minutes after presale ends
        } else {
          publicStartTime = Math.floor(Date.now() / 1000) + 660; // Public in 11 minutes
        }
      }

      // Validate timestamp order: presale start < presale end < public launch
      const now = Math.floor(Date.now() / 1000);

      if (presaleStartTime <= now) {
        toast.error("Presale start time must be in the future", {
          id: "timestamp-error",
          duration: 5000,
        });
        return;
      }

      if (presaleEndTime <= presaleStartTime) {
        toast.error("Presale end time must be after presale start time", {
          id: "timestamp-error",
          duration: 5000,
        });
        return;
      }

      if (publicStartTime <= presaleEndTime) {
        toast.error("Public launch time must be after presale end time", {
          id: "timestamp-error",
          duration: 5000,
        });
        return;
      }

      // Additional validation: ensure reasonable time gaps
      const minPresaleDuration = 5 * 60; // 5 minutes minimum
      const minGapBetweenPhases = 1 * 60; // 1 minute minimum gap

      if (presaleEndTime - presaleStartTime < minPresaleDuration) {
        toast.error("Presale must run for at least 5 minutes", {
          id: "timestamp-error",
          duration: 5000,
        });
        return;
      }

      if (publicStartTime - presaleEndTime < minGapBetweenPhases) {
        toast.error(
          "There must be at least 1 minute between presale end and public launch",
          {
            id: "timestamp-error",
            duration: 5000,
          }
        );
        return;
      }

      // Convert vesting schedule to seconds
      const vestingScheduleMap: { [key: string]: number } = {
        "1": 24 * 60 * 60, // 1 day
        "3": 3 * 24 * 60 * 60, // 3 days
        "5": 5 * 24 * 60 * 60, // 5 days
        " ": 30 * 24 * 60 * 60, // Custom - default to 30 days
      };
      const presaleVestingScheduleMap: { [key: string]: number } = {
        "1": 24 * 60 * 60, // 1 day
        "3": 3 * 24 * 60 * 60, // 3 days
        "5": 5 * 24 * 60 * 60, // 5 days
        " ": 7 * 24 * 60 * 60, // Custom - default to 7 days for presale
      };
      // Ensure allocation percentages sum to 80% as required by smart contract
      const presaleAllocation = Number(formData.presaleAllocationPercent) || 60;
      const creatorAllocation = Number(formData.creatorAllocationPercent) || 20;

      // Use the validated values (we already checked they sum to 80 above)
      const finalCreatorAllocation = creatorAllocation;

      const payload = {
        name: formData.tokenName,
        symbol: formData.tokenSymbol,
        description: formData.tokenDescription,
        creatorPublicKey: publicKey?.toBase58() || "",
        extensions: {
          website: formData.website || "",
          twitter: formData.twitter || "",
          telegram: formData.telegram || "",
        },
        presaleStartTime,
        presaleEndTime,
        publicStartTime,
        presaleAllocationPercent: presaleAllocation,
        creatorAllocationPercent: finalCreatorAllocation,
        userPresaleLimitPercent: formData.userPresaleLimitPercent || 50,
        cliffTimeSeconds:
          presaleVestingScheduleMap[formData.presaleVestingSchedule] ||
          7 * 24 * 60 * 60,
        vestingPeriodSeconds:
          vestingScheduleMap[formData.vestingSchedule] || 30 * 24 * 60 * 60,
      };
      Object.entries(payload).forEach(([key, value]) => {
        if (typeof value === "object" && value !== null) {
          apiFormData.append(key, JSON.stringify(value));
        } else {
          apiFormData.append(key, String(value));
        }
      });

      toast.loading("Launching your token...", { id: "token-creation" });

      const response = await fetch(
        "https://api.believein.fun/api/builder-transactions/launch",
        {
          method: "POST",
          body: apiFormData,
        }
      );

      const responseData = await response.json();

      if (response.ok) {
        if (responseData.success && responseData.data?.tokenMint?.publicKey) {
          const tokenAddress = responseData.data.tokenMint.publicKey;
          const { serializedTransaction, lastValidBlockHeight } =
            responseData.data;

          toast.loading("Signing and submitting transaction...", {
            id: "token-creation",
          });

          try {
            // 1. Deserialize the transaction
            const transaction = Transaction.from(
              Buffer.from(serializedTransaction, "base64")
            );

            // 2. Sign the transaction with user's wallet
            if (!signTransaction) {
              throw new Error("Wallet does not support transaction signing!");
            }
            const signedTransaction = await signTransaction(transaction);

            // 3. Submit to Solana network
            const signature = await connection.sendRawTransaction(
              signedTransaction.serialize(),
              { maxRetries: 5 }
            );

            // 4. Wait for confirmation
            const { blockhash } = await connection.getLatestBlockhash(
              "finalized"
            );
            await connection.confirmTransaction(
              {
                blockhash,
                lastValidBlockHeight,
                signature,
              },
              "confirmed"
            );

            setTokenAddress(tokenAddress);
            toast.success("🎉 Token launched successfully!", {
              id: "token-creation",
              duration: 5000,
            });

            console.log("✅ Transaction confirmed:", signature);
            setOpen(true);
            methods.reset();
            setStep(1);
          } catch (txError) {
            console.error("💥 Transaction failed:", txError);
            toast.error("Failed to submit transaction. Please try again.", {
              id: "token-creation",
              duration: 6000,
            });
          }
        }
      } else {
        let errorMessage =
          responseData.error ||
          responseData.message ||
          "Failed to create token";

        if (!responseData.error && !responseData.message) {
          if (response.status === 400) {
            errorMessage = "Invalid request data";
          } else if (response.status === 401) {
            errorMessage = "Unauthorized. Please check your wallet connection";
          } else if (response.status === 403) {
            errorMessage = "Access forbidden. You may not have permission";
          } else if (response.status === 429) {
            errorMessage = "Too many requests. Please wait before trying again";
          } else if (response.status >= 500) {
            errorMessage = "Server error. Please try again later";
          }
        }

        toast.error(errorMessage, {
          id: "token-creation",
          duration: 6000,
        });

        console.error("API Error Details:", {
          status: response.status,
          statusText: response.statusText,
          body: responseData,
        });
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "An unexpected error occurred";
      console.error("💥 Error submitting form:", error);
      toast.error(errorMessage, {
        id: "token-creation",
        duration: 6000,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [connected, publicKey, methods]);

  const goNext = useCallback(async () => {
    if (step === TOTAL_STEPS) {
      await handleSubmit();
      return;
    }

    const isStepValid = await validateCurrentStep();
    if (isStepValid) {
      setStep((prev) => Math.min(prev + 1, TOTAL_STEPS));
    }
  }, [step, validateCurrentStep, handleSubmit]);

  const goBack = useCallback(() => {
    if (step > 1) setStep((prev) => prev - 1);
  }, [step]);

  const canGoBack = useMemo(() => step > 1, [step]);
  const isLastStep = useMemo(() => step === TOTAL_STEPS, [step]);

  const isCurrentStepValid = useMemo(() => {
    const errors = methods.formState.errors;
    const values = methods.getValues();

    switch (step) {
      case 1:
        return (
          !errors.tokenName &&
          !errors.tokenDescription &&
          !errors.tokenSymbol &&
          !errors.image &&
          values.tokenName &&
          values.tokenDescription &&
          values.tokenSymbol &&
          values.image &&
          values.image.length > 0
        );
      case 2:
        return (
          !errors.vestingSchedule &&
          !errors.marketCap &&
          !errors.presaleVestingSchedule &&
          !errors.presaleAllocationPercent &&
          !errors.creatorAllocationPercent &&
          !errors.userPresaleLimitPercent &&
          values.vestingSchedule &&
          values.marketCap &&
          values.presaleVestingSchedule &&
          values.presaleAllocationPercent &&
          values.creatorAllocationPercent &&
          values.userPresaleLimitPercent
        );
      case 3:
        return true;
      default:
        return false;
    }
  }, [step, methods.formState.errors, methods.watch()]);

  return (
    <>
      <div className="flex justify-between items-center my-8">
        <ProgressBar currentStep={step} />
      </div>
      <FormProvider {...methods}>
        <div className="w-full mt-16 mx-auto p-8 space-y-6 border-2 border-primaryborder bg-background rounded-2xl">
          {currentStepComponent}
          {/* Navigation Buttons */}
          <div className="mt-6 flex gap-4 justify-end">
            {canGoBack && (
              <Button
                onClick={goBack}
                disabled={isSubmitting}
                variant="default"
                className="text-white"
                type="button"
              >
                <IconChevronLeft stroke={2} size={14} /> Back
              </Button>
            )}
            <Button
              onClick={goNext}
              disabled={isSubmitting || !isCurrentStepValid}
              className="bg-[#1F75FE] px-8 rounded-full text-white disabled:opacity-50 disabled:cursor-not-allowed"
              type="button"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Creating...
                </>
              ) : (
                <>
                  {isLastStep ? "Launch" : "Next"}
                  {isLastStep ? (
                    <Icons.FireWhite />
                  ) : (
                    <IconChevronRight stroke={2} size={14} />
                  )}
                </>
              )}
            </Button>
          </div>
        </div>
      </FormProvider>
      <TokenSuccessModalBuilder
        open={open}
        onOpenChange={setOpen}
        tokenAddress={tokenAddress}
      />
    </>
  );
};

export default TokenBuildForm;
